import React, { useState,useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  IconButton,
  CircularProgress,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { getUserById } from "../../../services/userService";
// Updated StepperContainer for vertical layout
const StepperContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  padding: theme.spacing(2),
  minWidth: "300px",
  borderRight: "1px solid rgba(0, 0, 0, 0.12)",
}));
const StyledTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontFamily: "Poppins",
  fontSize: "34px",
  fontWeight: 400,
  lineHeight: "32px",
  letterSpacing: "-0.5px",
  textAlign: "left",
  textUnderlinePosition: "from-font",
  textDecorationSkipInk: "none",
  color: "var(--Schemes-Secondary, #625B71)",
}));

const StepIcon = styled(IconButton)(({ theme, active, completed }) => ({
  width: 50,
  height: 50,
  backgroundColor:
    active || completed ? theme.palette.primary.main : theme.palette.grey[200],
  color:
    active || completed ? theme.palette.common.white : theme.palette.grey[500],
  "&:hover": {
    backgroundColor:
      active || completed
        ? theme.palette.primary.dark
        : theme.palette.grey[300],
  },
  "& svg": {
    fontSize: 24,
  },
}));

// Updated StepConnector for vertical layout
const StepConnector = styled(Box)(({ theme, active }) => ({
  width: 4,
  height: 50,
  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.grey[200],
  margin: "4px 0",
}));

// Rest of the imports and constants remain the same...
// [Previous companySizes array and other constants remain unchanged]

const companySizes = [
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201+", label: "201+ employees" },
];

const UserOnboarding = () => {
  // States and handlers remain the same...
  // [Previous state and handlers remain unchanged]
  const userId = useSelector((state) => state.appreducer.account.userId);
  const [userDetails, setUserDetails] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    companyName: "",
    companySize: "",
    industry: "",
    position: "",
    companyId: "",
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const userData = await getUserById(userId);
        setUserDetails(userData);
        setFormData(prev => ({
          ...prev,
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          email: userData.email || "",
        }));
      } catch (err) {
        console.error("Error fetching user details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId]);


  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
  };

  const CustomStepper = () => (
    <StepperContainer>
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <StepIcon active={activeStep >= 0} completed={activeStep > 0}>
          <PersonIcon />
        </StepIcon>
        <StepConnector active={activeStep > 0} />
        <StepIcon active={activeStep >= 1} completed={activeStep > 1}>
          <BusinessIcon />
        </StepIcon>
        <StepConnector active={activeStep > 1} />
        <StepIcon active={activeStep >= 2} completed={activeStep > 2}>
          <WorkIcon />
        </StepIcon>
      </Box>
    </StepperContainer>
  );

  // Previous render functions remain the same...
  // [Previous renderPersonalDetails, renderCompanyDetails, and renderJobProfile remain unchanged]

  const renderPersonalDetails = () => (
    <Box>
      <StyledTypography>Personal Details</StyledTypography>
      <Box
        sx={{
          padding: "40px",
          display: "grid",
          gap: 3,
          minHeight: "300px",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        }}
      >
        <TextField
          fullWidth
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={handleInputChange}
          required
          variant="outlined"
        />
        <TextField
          fullWidth
          label="Last Name"
          name="lastName"
          value={formData.lastName}
          onChange={handleInputChange}
          required
          variant="outlined"
        />
        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          required
          variant="outlined"
          sx={{ gridColumn: { sm: "1 / -1" } }}
        />
        <TextField
          fullWidth
          label="Phone Number"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          required
          variant="outlined"
          sx={{ gridColumn: { sm: "1 / -1" } }}
        />
      </Box>
    </Box>
  );

  const renderCompanyDetails = () => (
    <Box>
      <StyledTypography>Company Details</StyledTypography>
      <Box
        sx={{
          padding: "40px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          minHeight: "300px",
        }}
      >
        <TextField
          fullWidth
          label="Company Name"
          name="companyName"
          value={formData.companyName}
          onChange={handleInputChange}
          required
          variant="outlined"
        />
        <TextField
          fullWidth
          select
          label="Company Size"
          name="companySize"
          value={formData.companySize}
          onChange={handleInputChange}
          required
          variant="outlined"
        >
          {companySizes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          label="Industry"
          name="industry"
          value={formData.industry}
          onChange={handleInputChange}
          required
          variant="outlined"
        />
      </Box>
    </Box>
  );

  const renderJobProfile = () => (
    <Box>
      <StyledTypography>Job Profile</StyledTypography>
      <Box
        sx={{
          padding: "40px",
          display: "flex",
          flexDirection: "column",
          gap: 5,
          minHeight: "300px",
        }}
      >
        <TextField
          fullWidth
          label="Position in Company"
          name="position"
          value={formData.position}
          onChange={handleInputChange}
          required
          variant="outlined"
        />
        <TextField
          fullWidth
          label="Company ID Number"
          name="companyId"
          value={formData.companyId}
          onChange={handleInputChange}
          required
          variant="outlined"
        />
      </Box>
    </Box>
  );
  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.2)",
        borderRadius: "30px",
        background: "#FFFFFF",
        boxShadow: "6px 6px 54px 0px #0000000D",
        padding: "20px",
        minHeight: "70vh",
        minWidth: "60vw",
      }}
    >
      <Typography
        variant="h4"
        component="h2"
        align="start"
        gutterBottom
        sx={{
          fontFamily: "Raleway",
          fontSize: "24px",
          fontWeight: 600,
          lineHeight: "28.18px",
          textAlign: "left",
          textUnderlinePosition: "from-font",
          textDecorationSkipInk: "none",
        }}
      >
        User Onboarding
      </Typography>
      <Box sx={{ display: "flex", mt: 4 }}>
        <CustomStepper />

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            padding: "0 20px",
          }}
        >
          <Box sx={{ flex: 1, mb: 4 }}>
            {activeStep === 0 && renderPersonalDetails()}
            {activeStep === 1 && renderCompanyDetails()}
            {activeStep === 2 && renderJobProfile()}
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "end", mt: "20px", gap: 2 }}
          >
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{ px: 4 }}
            >
              Back
            </Button>
            {activeStep === 2 ? (
              <Button variant="contained" type="submit" sx={{ px: 4 }}>
                Submit
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext} sx={{ px: 4 }}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserOnboarding;
